<template>
  <!-- POC Cuidados Paliativos (Tela 4): plano de cuidados e diretivas
       antecipadas, no topo da Ficha. Salvo na API (/api/palliative). -->
  <section class="care-plan">
    <header class="cp-header">
      <div>
        <h2 class="cp-title">
          Plano de cuidados
        </h2>
        <p class="cp-subtitle">
          Visível para a equipe do hospital e para a equipe matricial.
          <span v-if="plan.updatedAt">
            Atualizado em {{ formatDateTime(plan.updatedAt) }}{{ plan.updatedBy ? ` por ${plan.updatedBy}` : "" }}.
          </span>
        </p>
      </div>
      <span
        class="cp-badge"
        :class="plan.directiveRegistered ? 'is-ok' : 'is-pending'"
      >{{ plan.directiveRegistered ? "Diretiva registrada" : "Sem diretiva registrada" }}</span>
    </header>

    <!-- Funcionalidade e objetivos -->
    <div class="cp-grid">
      <div class="cp-block">
        <lila-select
          v-model="plan.pps"
          label="Funcionalidade (PPS)"
          placeholder="Selecione"
          :options="ppsOptions"
        />
        <p class="cp-hint">
          PPS: escala de 0% a 100% de quanto o paciente consegue andar, se cuidar e se alimentar.
        </p>
      </div>
      <div class="cp-block cp-span-2">
        <lila-textarea
          v-model="plan.goals"
          label="Objetivos de cuidado"
          placeholder="Ex.: priorizar conforto e controle da dor; estar em casa com a família."
        />
      </div>
    </div>

    <!-- Preferências de local -->
    <div class="cp-grid cp-grid-2">
      <fieldset class="cp-block">
        <legend class="cp-label">
          Onde prefere ser cuidado
        </legend>
        <div class="cp-pills">
          <label
            v-for="option in placeOptions"
            :key="`care-${option._id}`"
            class="cp-pill"
            :class="{ 'is-selected': plan.carePlace === option._id }"
          >
            <input
              v-model="plan.carePlace"
              type="radio"
              class="cp-visually-hidden"
              :name="`carePlace-${patientId}`"
              :value="option._id"
            >
            {{ option.name }}
          </label>
        </div>
      </fieldset>

      <fieldset class="cp-block">
        <legend class="cp-label">
          Onde prefere estar no fim da vida
        </legend>
        <div class="cp-pills">
          <label
            v-for="option in placeOptions"
            :key="`death-${option._id}`"
            class="cp-pill"
            :class="{ 'is-selected': plan.deathPlace === option._id }"
          >
            <input
              v-model="plan.deathPlace"
              type="radio"
              class="cp-visually-hidden"
              :name="`deathPlace-${patientId}`"
              :value="option._id"
            >
            {{ option.name }}
          </label>
        </div>
      </fieldset>
    </div>

    <!-- Medidas de suporte -->
    <fieldset class="cp-block">
      <legend class="cp-label">
        Medidas de suporte
      </legend>
      <p class="cp-hint cp-hint-top">
        O que o paciente (ou a família, quando ele não puder decidir) aceita receber.
      </p>
      <table class="cp-measures">
        <tbody>
          <tr
            v-for="measure in supportMeasures"
            :key="measure._id"
          >
            <th scope="row">
              {{ measure.name }}
            </th>
            <td>
              <div
                class="cp-segmented"
                role="radiogroup"
                :aria-label="measure.name"
              >
                <label
                  v-for="decision in supportDecisions"
                  :key="decision._id"
                  class="cp-segment"
                  :class="[`is-${decision._id}`, { 'is-selected': plan.supportMeasures[measure._id] === decision._id }]"
                >
                  <input
                    v-model="plan.supportMeasures[measure._id]"
                    type="radio"
                    class="cp-visually-hidden"
                    :name="`measure-${measure._id}-${patientId}`"
                    :value="decision._id"
                  >
                  {{ decision.name }}
                </label>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </fieldset>

    <!-- Diretivas antecipadas: o registro completo só aparece com a opção marcada -->
    <div class="cp-block cp-directive">
      <label class="cp-check cp-check-title">
        <input
          v-model="plan.directiveRegistered"
          type="checkbox"
          @change="onDirectiveToggle"
        >
        Diretivas antecipadas
      </label>
      <p
        v-if="!plan.directiveRegistered"
        class="cp-hint"
      >
        Marque para registrar a conversa com o paciente sobre o que ele aceita ou não receber.
      </p>

      <div
        v-else
        class="cp-directive-body"
      >
        <p class="cp-hint cp-hint-top">
          O registro do médico no prontuário vale como diretiva (Resolução CFM 1.995/2012). As medidas de
          suporte acima fazem parte dela.
        </p>

        <div class="cp-grid cp-grid-2">
          <div class="cp-block">
            <lila-datepicker
              v-model="plan.directiveDate"
              label="Data da conversa"
              placeholder="Selecione"
            />
          </div>
          <div class="cp-block">
            <lila-input
              v-model="plan.directive.doctorName"
              label="Médico responsável"
              placeholder="Nome"
            />
          </div>
        </div>

        <fieldset class="cp-block">
          <legend class="cp-label">
            Quem participou
          </legend>
          <div class="cp-pills">
            <label
              v-for="option in participantOptions"
              :key="option._id"
              class="cp-pill"
              :class="{ 'is-selected': plan.directive.participants.includes(option._id) }"
            >
              <input
                v-model="plan.directive.participants"
                type="checkbox"
                class="cp-visually-hidden"
                :value="option._id"
              >
              {{ option.name }}
            </label>
          </div>
        </fieldset>

        <fieldset class="cp-block">
          <legend class="cp-label">
            O paciente tinha condição de decidir?
          </legend>
          <div class="cp-pills">
            <label
              v-for="option in yesNo"
              :key="option.value"
              class="cp-pill"
              :class="{ 'is-selected': plan.directive.patientCouldDecide === option.value }"
            >
              <input
                v-model="plan.directive.patientCouldDecide"
                type="radio"
                class="cp-visually-hidden"
                :name="`couldDecide-${patientId}`"
                :value="option.value"
              >
              {{ option.name }}
            </label>
          </div>
          <div
            v-if="plan.directive.patientCouldDecide === false"
            class="cp-follow-up"
          >
            <lila-input
              v-model="plan.directive.decidedBy"
              label="Quem decidiu por ele"
              placeholder="Nome e vínculo (ex.: Ana, filha)"
            />
          </div>
        </fieldset>

        <div class="cp-block">
          <p class="cp-label">
            Representante indicado pelo paciente
          </p>
          <div class="cp-grid cp-grid-2">
            <lila-input
              v-model="plan.directive.proxyName"
              placeholder="Nome"
            />
            <lila-input
              v-model="plan.directive.proxyRelation"
              placeholder="Vínculo (ex.: filha)"
            />
          </div>
          <p class="cp-hint">
            O procurador de saúde: quem decide pelo paciente quando ele não puder.
          </p>
        </div>

        <div class="cp-block">
          <lila-textarea
            v-model="plan.directive.wishes"
            label="Valores e desejos"
            placeholder="Ex.: não quer ser intubado; quer estar em casa e receber os netos."
          />
        </div>

        <div class="cp-directive-footer">
          <!-- Gera a via no navegador, com o que está na tela (imprimir ou salvar em PDF). -->
          <button
            type="button"
            class="cp-secondary-button"
            @click="generateDocument"
          >
            Gerar documento
          </button>
          <p class="cp-hint cp-hint-inline">
            Para imprimir e assinar, ou salvar em PDF, quando a instituição pedir uma via em papel. Documento
            de cartório, se a família tiver, vai na aba Anexos.
            <strong v-if="hasUnsavedChanges">Há alterações não salvas: o documento sai com o que está na tela.</strong>
          </p>
        </div>

        <div
          v-if="plan.directive.versions.length > 0"
          class="cp-versions"
        >
          <p class="cp-versions-title">
            Histórico das diretivas
          </p>
          <ol class="cp-versions-list">
            <li
              v-for="(version, index) in [...plan.directive.versions].reverse()"
              :key="version.savedAt"
            >
              <strong>Versão {{ plan.directive.versions.length - index }}</strong>
              · {{ formatDateTime(version.savedAt) }}{{ version.by ? ` por ${version.by}` : "" }}
            </li>
          </ol>
        </div>
      </div>
    </div>

    <!-- Cuidadora -->
    <div class="cp-grid cp-grid-2">
      <div class="cp-block">
        <p class="cp-label">
          Cuidadora principal
        </p>
        <lila-input
          v-model="plan.caregiverName"
          placeholder="Nome"
        />
        <lila-input
          v-model="plan.caregiverRelation"
          placeholder="Vínculo (ex.: filha)"
        />
      </div>
    </div>

    <div class="cp-actions">
      <lila-button
        type="button"
        title="Salvar plano"
        @custom-click-event="save"
      />
      <p
        v-if="justSaved"
        class="cp-saved"
        role="status"
      >
        Plano salvo.
      </p>
      <p
        v-if="saveError"
        class="cp-save-error"
        role="alert"
      >
        {{ saveError }}
      </p>
    </div>
  </section>
</template>

<script>
import Button from "@components/shared/button/Button.vue";
import Input from "@components/shared/fields/Input.vue";
import Select from "@components/shared/fields/Select.vue";
import Textarea from "@components/shared/fields/Textarea.vue";
import LilaDatepicker from "@components/shared/fields/LilaDatepicker.vue";
import { eventBus } from "@/eventBus.js";
import {
  CARE_PLAN_UPDATED_EVENT,
  DIRECTIVE_PARTICIPANTS,
  PLACE_OPTIONS,
  PPS_OPTIONS,
  SUPPORT_DECISIONS,
  SUPPORT_MEASURES,
  emptyCarePlan,
  getCarePlan,
  saveCarePlan
} from "@/services/palliative/carePlanApi";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";
import { printDirectiveDocument } from "@/services/palliative/directiveDocument";

export default {
  name: "PalliativeCarePlan",
  components: {
    "lila-button": Button,
    "lila-input": Input,
    "lila-select": Select,
    "lila-textarea": Textarea,
    "lila-datepicker": LilaDatepicker
  },
  // Plano salvo por outro profissional: recarrega, a não ser que haja edição em andamento aqui.
  mixins: [pocLiveSync("reloadIfUntouched")],
  props: {
    patientId: {
      type: [String, Number],
      required: true
    },
    // Acompanhante da Ficha: sugere a cuidadora quando o plano ainda está vazio.
    companionName: {
      type: String,
      default: ""
    },
    // Dados da Ficha para o documento das diretivas: { name, cpf, cns, birthDate }.
    patient: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["saved"],
  data() {
    return {
      plan: emptyCarePlan(),
      justSaved: false,
      saving: false,
      saveError: "",
      // Plano como veio da API: base para saber se há alterações não salvas.
      loadedSnapshot: "",
      ppsOptions: PPS_OPTIONS,
      placeOptions: PLACE_OPTIONS,
      supportMeasures: SUPPORT_MEASURES,
      supportDecisions: SUPPORT_DECISIONS,
      participantOptions: DIRECTIVE_PARTICIPANTS,
      yesNo: [
        { value: true, name: "Sim" },
        { value: false, name: "Não" }
      ]
    };
  },
  computed: {
    hasUnsavedChanges() {
      return !!this.loadedSnapshot && JSON.stringify(this.plan) !== this.loadedSnapshot;
    }
  },
  watch: {
    patientId: {
      immediate: true,
      handler() {
        this.load();
      }
    },
    companionName(name) {
      if (name && !this.plan.caregiverName && !this.plan.updatedAt) {
        this.plan.caregiverName = name;
      }
    }
  },
  methods: {
    async load() {
      const patientId = this.patientId;
      let saved = null;
      try {
        saved = await getCarePlan(patientId);
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o plano de cuidados.", error);
        return;
      }
      if (String(patientId) !== String(this.patientId)) return;
      this.plan = saved || { ...emptyCarePlan(), caregiverName: this.companionName || "" };
      this.loadedSnapshot = JSON.stringify(this.plan);
    },
    reloadIfUntouched() {
      if (!this.saving && JSON.stringify(this.plan) === this.loadedSnapshot) this.load();
    },
    // Ao marcar "Diretivas antecipadas", sugere a data de hoje e quem está logado.
    onDirectiveToggle() {
      if (!this.plan.directiveRegistered) return;
      if (!this.plan.directiveDate) this.plan.directiveDate = this.$moment().format("DD/MM/YYYY");
      if (!this.plan.directive.doctorName) {
        const session = this.$Session.get(this.$userSessionName);
        this.plan.directive.doctorName = session?.professional?.Name || "";
      }
    },
    generateDocument() {
      printDirectiveDocument(this.plan, this.patient);
    },
    formatDateTime(iso) {
      return this.$moment(iso).format("DD/MM/YYYY [às] HH:mm");
    },
    // A API grava o plano, cria uma nova versão da diretiva quando ela muda e
    // gera o procedimento de planejamento antecipado na Produção SUS.
    async save() {
      if (this.saving) return;
      this.saving = true;
      this.saveError = "";
      try {
        this.plan = await saveCarePlan(this.patientId, this.plan);
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível salvar o plano de cuidados.", error);
        this.saveError = error.message;
        return;
      } finally {
        this.saving = false;
      }

      this.loadedSnapshot = JSON.stringify(this.plan);
      eventBus.emit(CARE_PLAN_UPDATED_EVENT, { patientId: String(this.patientId) });
      this.$emit("saved");

      this.justSaved = true;
      clearTimeout(this.savedTimeout);
      this.savedTimeout = setTimeout(() => (this.justSaved = false), 3000);
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #4f3f82;
$primary-soft: #ece9f5;
$ink: #2b2b3a;
$label: #484848;
$muted: #6b6b7b;
$border: #dcd8e6;

.care-plan {
  margin-bottom: 32px;
  padding: 24px;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.08);
}

.cp-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.cp-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: $primary;
}

.cp-subtitle {
  margin: 0;
  font-size: 13px;
  color: $muted;
}

.cp-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: currentColor;
  }

  &.is-ok {
    color: #1e6b3a;
    background-color: #e3f4e8;
  }

  &.is-pending {
    color: #8a5a00;
    background-color: #fff3d6;
  }
}

.cp-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 24px;
}

.cp-grid-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cp-span-2 {
  grid-column: span 2;
}

.cp-block {
  min-width: 0;
  margin: 0 0 24px;
  padding: 0;
  border: 0;

  :deep(.field) {
    margin-bottom: 8px;
  }

  :deep(.label) {
    margin: 0 0 10px 20px;
    font-size: 16px;
    font-weight: 700;
    color: $label;
  }
}

.cp-label {
  display: block;
  margin: 0 0 10px 20px;
  padding: 0;
  font-size: 16px;
  line-height: 18px;
  font-weight: 700;
  color: $label;
}

.cp-hint {
  margin: 6px 0 0 20px;
  font-size: 12px;
  line-height: 16px;
  color: $muted;
}

.cp-hint-top {
  margin: -4px 0 12px 20px;
}

.cp-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.cp-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cp-pill {
  position: relative;
  padding: 8px 16px;
  border: 1px solid $border;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  background-color: #ffffff;
  cursor: pointer;

  &:hover {
    background-color: $primary-soft;
  }

  &.is-selected {
    border-color: $primary;
    color: #ffffff;
    background-color: $primary;
  }

  &:focus-within {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}

.cp-measures {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 0;
    border-bottom: 1px solid #f0eef5;
    vertical-align: middle;
  }

  th {
    padding-left: 20px;
    font-size: 14px;
    font-weight: 600;
    color: $ink;
    text-align: left;
  }

  td {
    text-align: right;
  }

  tr:last-child th,
  tr:last-child td {
    border-bottom: 0;
  }
}

.cp-segmented {
  display: inline-flex;
  border: 1px solid $border;
  border-radius: 999px;
  overflow: hidden;
}

.cp-segment {
  position: relative;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: $muted;
  background-color: #ffffff;
  cursor: pointer;

  & + & {
    border-left: 1px solid $border;
  }

  &:hover {
    background-color: #f7f6fb;
  }

  &:focus-within {
    outline: 2px solid $primary;
    outline-offset: -2px;
  }

  &.is-selected.is-sim {
    color: #1e6b3a;
    background-color: #e3f4e8;
  }

  &.is-selected.is-nao {
    color: #9b1c1c;
    background-color: #fbe4e4;
  }

  &.is-selected.is-discutir {
    color: #8a5a00;
    background-color: #fff3d6;
  }
}

.cp-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px 20px;
  font-size: 14px;
  color: $ink;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: $primary;
  }
}

/* Diretivas antecipadas */
.cp-directive {
  padding-top: 20px;
  border-top: 1px solid #f0eef5;
}

.cp-check-title {
  font-size: 16px;
  font-weight: 700;
  color: $label;
}

.cp-directive-body {
  margin: 12px 0 0 20px;
  padding: 16px 0 0 20px;
  border-left: 3px solid $primary-soft;

  .cp-grid-2 {
    gap: 0 16px;
  }
}

.cp-follow-up {
  max-width: 420px;
  margin-top: 12px;
}

.cp-directive-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.cp-hint-inline {
  flex: 1;
  min-width: 240px;
  margin: 0;
}

.cp-secondary-button {
  padding: 8px 18px;
  border: 1px solid $primary;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  background-color: #ffffff;
  cursor: pointer;

  &:hover {
    background-color: $primary-soft;
  }
}

.cp-versions {
  margin-top: 16px;
}

.cp-versions-title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 700;
  color: $label;
}

.cp-versions-list {
  margin: 0;
  padding: 0 !important;
  list-style: none;

  li {
    font-size: 13px;
    line-height: 20px;
    color: $muted;
  }

  strong {
    color: $ink;
  }
}

.cp-save-error {
  margin: 0;
  color: #db466f;
  font-size: 13px;
}

.cp-actions {
  display: flex;
  align-items: center;
  gap: 16px;

  :deep(.field) {
    margin-bottom: 0;
  }
}

.cp-saved {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e6b3a;
}

@media (max-width: 1024px) {
  .cp-grid,
  .cp-grid-2 {
    grid-template-columns: minmax(0, 1fr);
  }

  .cp-span-2 {
    grid-column: auto;
  }

  .cp-measures td {
    text-align: left;
  }
}
</style>
